import { captureException } from "@sentry/browser"
import createDefaultWait from "../../../createDefaultWait"
import { deepClone } from "../../../deep_clone"
import BotInvalidMove from "../../../Exception/BotInvalidMove.class"
import createDraftMove from "../../draft_move/createDraftMove"
import type { Engine } from "../../engine/Engine.type"
import type { AnnexableEntityUnion } from "../../entity/AnnexableEntityUnion.type"
import canUnitDirectAttackInGeneral from "../../entity/canUnitDirectAttackInGeneral"
import { byEntityResaleValueDesc } from "../../entity/getEntityResaleValue"
import { byEntityMapControlPriorityDesc } from "../../entity/getMapControlPriority"
import getPlayerEntities from "../../entity/getPlayerEntities"
import isBeingAnnexed from "../../entity/isBeingAnnexed"
import isEntityAnnexable from "../../entity/isEntityAnnexable"
import isEntityHasMovesLeft from "../../entity/isEntityHasMovesLeft"
import isEntityUnit from "../../entity/isEntityUnit"
import notInTaxi from "../../entity/notInTaxi"
import type { UnitEntityUnion } from "../../entity/UnitEntityUnion.type"
import { createMoveUnitAction } from "../../state/flux/action/Game/MoveUnitAction"
import dispatchClient from "../../state/flux/dispatchClient"
import createEntityGridXYV2 from "../../tile_position_xy/createEntityGridXYV2"
import findMostEfficientPathToTileV2 from "../../tile_position_xy/findMostEfficientPathToTileV2/findMostEfficientPathToTileV2"
import type { PathTurnStepCollection } from "../../tile_position_xy/findMostEfficientPathToTileV2/PathTurnStepCollection.type"
import getManhattanDistance from "../../tile_position_xy/getManhattanDistance"
import { byDistanceAsc } from "../../tile_position_xy/sortByDistance"
import toCoord from "../../tile_position_xy/toCoord"
import { byTotalCostAsc } from "../../util/sortByTotalCost"
import type { Bot } from "../Bot.type"
import throwOnBotError from "../throwOnBotError"
import botShouldContinue from "./botShouldContinue"

type ApproachableEntityAndDistance = {
  ent: AnnexableEntityUnion | UnitEntityUnion
  distance: number
}

/**
 * all unit that can attacks shall seek out enemies,
 * when no better options are available,
 * 
 * @param engine 
 * @param bot 
 */
export default async function botDirectAttackFind(
  engine: Engine,
  bot: Bot
) {
  console.log('%c[botDirectAttackFind]', 'color: grey; font-weight: lighter; font-size: 10px;')
  const { player, stepDelay } = bot
  const playerId = player.id
  const allPlayerEnts = getPlayerEntities(engine.state.ents, playerId)
  // console.log('allPlayerEnts', deepClone(allPlayerEnts))
  const playerUnits = allPlayerEnts.filter((ent) => {
    return (
      isEntityUnit(ent) &&
      isEntityHasMovesLeft(ent) &&
      notInTaxi(ent) &&
      canUnitDirectAttackInGeneral(ent)
    )
  }).sort(byEntityResaleValueDesc)
  // console.log('playerInfantryUnits', deepClone(playerUnits))

  const { width, height } = engine.state

  async function innerFn(unit: typeof playerUnits[0]) {

    const entityGridXY = createEntityGridXYV2(engine.state.ents, width, height)

    const allApproachableEnts: Array<AnnexableEntityUnion> = engine.state.ents.filter((ent): ent is AnnexableEntityUnion => {
      if (ent.player_id === playerId) return false

      if (isEntityAnnexable(ent) && !isBeingAnnexed(ent)) {
        return true
      }
      if (isEntityUnit(ent)) {
        return true
      }
      return false
    }).sort(byEntityMapControlPriorityDesc)
    console.log('allApproachableEnts', deepClone(allApproachableEnts))

    const bestPaths = allApproachableEnts.reduce((carry: Array<ApproachableEntityAndDistance>, approachableEnt: AnnexableEntityUnion) => {
      carry.push({
        ent: approachableEnt,
        distance: getManhattanDistance(unit, approachableEnt),
      })
      return carry
    }, []).sort(byDistanceAsc).reduce((carry: Array<PathTurnStepCollection>, md) => {
      if (carry.length < 8) {
        const { ent: annexableEnt } = md
        const eptt = findMostEfficientPathToTileV2(entityGridXY, unit, width, height, annexableEnt.x, annexableEnt.y)
        if (eptt) {
          carry.push(eptt)
        }
      }
      return carry
    }, []).sort(byTotalCostAsc)

    // hope the first lastStep is not occupied
    let index = 1
    do {
      for (const eptt of bestPaths) {
        if (await attemptEptt(unit, eptt, index)) {
          return
        }
      }
    } while (index++ <= unit.mobility)
  }

  async function attemptEptt(unit: typeof playerUnits[0], eptt: PathTurnStepCollection, index: number): Promise<void | true> {
    const firstTurn = eptt.turns[0]
    if (firstTurn) {
      const lastStep = firstTurn.steps.at(index * -1)
      if (lastStep && !lastStep?.impass && !lastStep?.occupant && !lastStep?.enemies?.[0]) {
        try {
          const draftMove = createDraftMove(unit)
          draftMove.destPosition = toCoord(lastStep)
          await dispatchClient(engine, createMoveUnitAction(draftMove))

          // stop attempting to find a place to go
          return true
        } catch (err1) {
          const err2 = new BotInvalidMove('Stroll', err1)
          console.error(err2)
          captureException(err2)
          throwOnBotError(err2)
        }
      }
    }
  }

  do {
    const unit = playerUnits.pop()
    if (!unit) {
      console.log('%cran out of units', 'color: grey; font-weight: lighter; font-size: 10px;')
      break
    }

    await innerFn(unit)

    await createDefaultWait(stepDelay)
  } while (botShouldContinue(engine, bot))
}